import type { ReactNode } from 'react';
import { Flex, Box, type FlexProps } from '@chakra-ui/react';

export interface PillItem<T extends string | number = string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

interface PillGroupProps<T extends string | number> extends Omit<FlexProps, 'onChange'> {
  items: PillItem<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  /** Visual density. Defaults to 'md' (original sizing) — unchanged for existing callers. */
  size?: 'sm' | 'md';
}

const PILL_SIZE_STYLES = {
  md: { px: 6, py: 3, fontSize: 'md' },
  sm: { px: 4, py: 2, fontSize: 'sm' },
} as const;

/**
 * DS PillGroup (Navigation card) — a single-select toggle row of pills used for
 * content-type / segment selectors. Active pill = brand fill + brand.contrast
 * label; inactive = white surface (bg.panel) + strong border. All values come
 * straight from DS tokens — no raw numbers.
 *
 * Composed from primitives rather than Chakra's SegmentGroup on purpose: the DS
 * metaphor is separate, gapped, brand-filled pills, not a connected track with a
 * sliding indicator. Pills render as buttons (aria-pressed) for keyboard/AT use.
 */
export function PillGroup<T extends string | number>({
  items,
  value,
  onChange,
  size = 'md',
  ...rest
}: PillGroupProps<T>) {
  const sizeStyles = PILL_SIZE_STYLES[size];
  return (
    <Flex flexWrap='wrap' gap={3} {...rest}>
      {items.map((item) => {
        const isActive = item.value === value;
        return (
          <Box
            as='button'
            type='button'
            key={item.value}
            disabled={item.disabled}
            aria-pressed={isActive}
            onClick={() => onChange(item.value)}
            px={sizeStyles.px}
            py={sizeStyles.py}
            borderRadius='md'
            borderWidth='1px'
            fontFamily='body'
            fontSize={sizeStyles.fontSize}
            fontWeight='600'
            lineHeight='1'
            whiteSpace='nowrap'
            transition='background-color .12s, color .12s, border-color .12s'
            // Same DS brand fill as the primary button / home filter chips:
            // --brand-primary (the rest stop), not brand.solid (brand.600, one
            // stop dark). theme.ts keeps the var in sync with the workspace.
            bg={isActive ? 'var(--brand-primary)' : 'bg.panel'}
            color={isActive ? 'brand.contrast' : 'fg.strong'}
            borderColor={isActive ? 'var(--brand-primary)' : 'border.emphasized'}
            cursor='pointer'
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          >
            {item.label}
          </Box>
        );
      })}
    </Flex>
  );
}

export default PillGroup;
